import React from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useDebouncedValue } from './useUrlPagination';

// Live "email already in use?" check shared by the People dialogs and the Admin
// Panel user dialogs. Debounced; the server compares (case-insensitively) against
// all people (drivers/staff/passengers) AND all user accounts.
const EMAIL_RX = /^\S+@\S+\.\S+$/;

export const useEmailAvailability = (api, email, { excludePersonId, excludeUserId, enabled } = {}) => {
  const [result, setResult] = React.useState(null);
  const debounced = useDebouncedValue(email, 500);
  React.useEffect(() => {
    const e = (debounced || '').trim();
    if (!enabled || !e || !EMAIL_RX.test(e)) { setResult(null); return undefined; }
    let cancelled = false;
    setResult({ checking: true });
    const params = new URLSearchParams({ email: e });
    if (excludePersonId) params.set('exclude_person_id', excludePersonId);
    if (excludeUserId) params.set('exclude_user_id', excludeUserId);
    api.get(`/people/email-available?${params.toString()}`)
      .then((res) => { if (!cancelled) setResult(res.data); })
      .catch(() => { if (!cancelled) setResult(null); });
    return () => { cancelled = true; };
  }, [api, debounced, excludePersonId, excludeUserId, enabled]);
  return result;
};

export const EmailAvailabilityHint = ({ result, testid }) => {
  if (!result) return null;
  if (result.checking) return <p className="text-[11px] text-muted-foreground" data-testid={`${testid}-checking`}>Checking email availability…</p>;
  if (result.available === false) return (
    <p className="text-[11px] text-red-600 flex items-center gap-1" data-testid={`${testid}-taken`}>
      <AlertTriangle className="w-3 h-3 shrink-0" /> {result.reason || 'This email is already in use.'}
    </p>
  );
  return (
    <p className="text-[11px] text-green-600 flex items-center gap-1" data-testid={`${testid}-free`}>
      <CheckCircle className="w-3 h-3 shrink-0" /> Email available
    </p>
  );
};
